import React, { useEffect, useState } from "react";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import clsx from "clsx";

import imgLoading from "asset/img/loading.gif";
import { updateMyProfile, updateAvatar } from "api/services/getUsers";
import { SET_MESSAGES_REDUCER } from "redux/reducers/messages/actionTypes";
import { UPDATE_CURRENT_USER } from "redux/reducers/auth/actionTypes";
import { UserReducer } from "redux/reducers/auth";
import customTheme from "config/theme";
import MdPhotos from "react-ionicons/lib/MdPhotos";
import MdCamera from "react-ionicons/lib/MdCamera";
import CustomAvatar from "components/Image/Avatar";
import CameraCaptureModal from "./CameraCaptureModal";
import styles from "./styles";

// Default avatars used by the avatar modal
const defaultAvatars = [
  "/uploads/avatars/avatar_default1.jpg",
  "/uploads/avatars/avatar_default2.jpg",
  "/uploads/avatars/avatar_default3.jpg",
  "/uploads/avatars/avatar_default4.jpg",
  "/uploads/avatars/avatar_default5.jpg",
  "/uploads/avatars/avatar_default1.png",
  "/uploads/avatars/avatar_default2.png",
  "/uploads/avatars/avatar_default3.png",
  "/uploads/avatars/avatar_default4.png",
  "/uploads/avatars/avatar_default5.png",
  "/uploads/avatars/avatar_default6.png",
  "/uploads/avatars/avatar_default7.png",
  "/uploads/avatars/avatar_default8.png",
  "/uploads/avatars/avatar_default9.png",
  "/uploads/avatars/avatar_default10.png",
  "/uploads/avatars/avatar_default11.png",
  "/uploads/avatars/avatar_default12.png",
  "/uploads/avatars/avatar_default14.png",
  "/uploads/avatars/avatar_default15.png",
  "/uploads/avatars/avatar_default16.png",
  "/uploads/avatars/avatar_default17.png",
  "/uploads/avatars/avatar_default18.png",
  "/uploads/avatars/avatar_default19.png",
  "/uploads/avatars/avatar_default20.png",
];

interface AvatarEditModalProps {
  open: boolean;
  onClose: () => void;
  user: UserReducer;
  localLocationText: string;
}

const AvatarEditModal = React.memo(
  (props: AvatarEditModalProps): React.ReactElement | null => {
    const { open, onClose, user, localLocationText } = props;

    const classes = styles();
    const translate = useTranslation().t;
    const dispatch = useDispatch();

    const getAvatarUrl = (avatarUrl: string | null): string | null => {
      if (!avatarUrl) return null;
      if (avatarUrl.startsWith("data:") || avatarUrl.startsWith("blob:")) {
        return avatarUrl;
      }
      const baseUrl = process.env.REACT_APP_DOMAIN_API || "";
      return `${baseUrl}${avatarUrl}`;
    };

    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(() => {
      // Keep preview stable even on the first render of the modal
      return (user as any)?.imageUrl || (user as any)?.avatar || null;
    });
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [selectedDefaultAvatar, setSelectedDefaultAvatar] = useState<
      string | null
    >(null);
    const [cameraModalOpen, setCameraModalOpen] = useState(false);

    useEffect(() => {
      // Preload default avatars to reduce modal "first open" delays.
      defaultAvatars.forEach((avatarUrl) => {
        const img = new Image();
        img.src = getAvatarUrl(avatarUrl) || "";
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (!open) return;

      setAvatarFile(null);
      setSelectedDefaultAvatar(null);
      setUploadingAvatar(false);
      setCameraModalOpen(false);
      setAvatarPreview(
        (user.imageUrl as any) || ((user as any).avatar as any) || null
      );
    }, [open, user]);

    const handleCloseAvatarModal = () => {
      setCameraModalOpen(false);
      onClose();
    };

    const handleSelectDefaultAvatar = (avatarUrl: string) => {
      setAvatarFile(null);
      setSelectedDefaultAvatar(avatarUrl);
      setAvatarPreview(avatarUrl);
    };

    const handleChooseFromDevice = () => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (e: any) => {
        const file = e.target.files?.[0];
        if (file) {
          const previewUrl = URL.createObjectURL(file);
          setAvatarFile(file);
          setSelectedDefaultAvatar(null);
          setAvatarPreview(previewUrl);
        }
      };
      input.click();
    };

    const handleChooseFromCamera = () => {
      setCameraModalOpen(true);
    };

    const handleCloseCameraModal = () => {
      setCameraModalOpen(false);
    };

    const handleUseCapturedImage = (dataUrl: string) => {
      fetch(dataUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
          const previewUrl = URL.createObjectURL(blob);
          setAvatarFile(file);
          setSelectedDefaultAvatar(null);
          setAvatarPreview(previewUrl);
          handleCloseCameraModal();
        })
        .catch(() => {
          dispatch({
            type: SET_MESSAGES_REDUCER,
            message: {
              type: "error",
              key: "IMAGE_CONVERSION_ERROR",
              message: "Failed to process image. Please try again.",
            },
          });
        });
    };

    const handleSaveAvatar = async () => {
      if (uploadingAvatar) return;

      if (!avatarFile && !selectedDefaultAvatar) {
        handleCloseAvatarModal();
        return;
      }

      setUploadingAvatar(true);
      try {
        let newAvatarUrl: string | null = null;

        if (avatarFile) {
          const res = await updateAvatar(avatarFile, user.id);
          if (res && res.imageUrl) {
            newAvatarUrl = res.imageUrl;
          }
        } else {
          const obj = {
            ...user,
            locationText: localLocationText,
            imageUrl: selectedDefaultAvatar,
          };
          const res = await updateMyProfile(obj);
          if (res && (res as any).id) {
            newAvatarUrl = selectedDefaultAvatar;
          }
        }

        if (newAvatarUrl) {
          dispatch({
            type: UPDATE_CURRENT_USER,
            current_user: {
              ...user,
              imageUrl: newAvatarUrl,
            } as any,
          });
          dispatch({
            type: SET_MESSAGES_REDUCER,
            message: {
              type: "success",
              key: "UPDATE_AVATAR_SUCCESSFULLY",
              message:
                translate("UPDATE_AVATAR_SUCCESSFULLY") ||
                "Avatar updated successfully",
            },
          });
        }

        handleCloseAvatarModal();
      } catch (error) {
        dispatch({
          type: SET_MESSAGES_REDUCER,
          message: {
            type: "error",
            key: "UPDATE_AVATAR_ERROR",
            message: translate("UPDATE_AVATAR_ERROR"),
          },
        });
      } finally {
        setUploadingAvatar(false);
      }
    };

    return (
      <>
        <Modal
          className={classes.modal}
          open={open}
          onClose={handleCloseAvatarModal}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={open}>
            <div
              className={clsx(classes.modalPaper, classes.avatarModalPaper)}
            >
              <div className={classes.avatarModalHeader}>
                {translate("EDIT_AVATAR")}
              </div>

              <div className={classes.avatarModalContent}>
                {avatarPreview && (
                  <div className={classes.previewContainer}>
                    <CustomAvatar
                      src={getAvatarUrl(avatarPreview)}
                      alt="Preview"
                      size={150}
                    />
                  </div>
                )}

                <div className={classes.defaultAvatarsContainer}>
                  <IconButton
                    onClick={handleChooseFromCamera}
                    style={{ padding: 0 }}
                  >
                    <Avatar className={classes.iconButtonAvatar}>
                      <MdCamera
                        color={(customTheme as any).bgButton}
                        fontSize="32px"
                      />
                    </Avatar>
                  </IconButton>

                  <IconButton
                    onClick={handleChooseFromDevice}
                    style={{ padding: 0 }}
                  >
                    <Avatar className={classes.iconButtonAvatar}>
                      <MdPhotos
                        color={(customTheme as any).bgButton}
                        fontSize="32px"
                      />
                    </Avatar>
                  </IconButton>

                  {defaultAvatars.map((avatarUrl, index) => (
                    <CustomAvatar
                      key={index}
                      src={getAvatarUrl(avatarUrl)}
                      className={classes.defaultAvatar}
                      onClick={() => handleSelectDefaultAvatar(avatarUrl)}
                    />
                  ))}
                </div>

                <div className={classes.modalActions}>
                  <Button
                    onClick={handleCloseAvatarModal}
                    variant="outlined"
                  >
                    {translate("CANCEL")}
                  </Button>

                  <Button
                    onClick={handleSaveAvatar}
                    variant="contained"
                    className={classes.saveButton}
                    disabled={avatarPreview == null}
                  >
                    {uploadingAvatar ? (
                      <img alt="loading" src={imgLoading} width="20px" />
                    ) : (
                      translate("SAVE")
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Fade>
        </Modal>

        <CameraCaptureModal
          open={cameraModalOpen}
          onClose={handleCloseCameraModal}
          onUseCapturedImage={handleUseCapturedImage}
        />
      </>
    );
  }
);

export default AvatarEditModal;

